export function formatAbhaNumber(abhaNumber) {
  return String(abhaNumber || "").replace(/(\d{2})(\d{4})(\d{4})(\d{4})/, "$1-$2-$3-$4");
}

export function normalizeBase64Image(value) {
  if (!value) return "";
  const trimmed = String(value).trim();

  if (/^data:image\/(png|jpeg|jpg|webp|gif);base64,/i.test(trimmed)) {
    return trimmed;
  }

  if (/^data:.*;base64,/i.test(trimmed)) {
    return trimmed.replace(/^data:[^;]+;base64,/i, "data:image/jpeg;base64,");
  }

  if (/^[A-Za-z0-9+/=\r\n]+$/.test(trimmed)) {
    return `data:image/jpeg;base64,${trimmed.replace(/\s+/g, "")}`;
  }

  return trimmed;
}

export function normalizeSearchGender(gender) {
  const g = String(gender || "").trim();
  if (g === "M" || g === "Male") return "MALE";
  if (g === "F" || g === "Female") return "FEMALE";
  if (g === "O" || g === "Others" || g === "Other") return "OTHER";
  if (g.toUpperCase() === "MALE") return "MALE";
  if (g.toUpperCase() === "FEMALE") return "FEMALE";
  if (g.toUpperCase() === "OTHER") return "OTHER";
  return g ? g.toUpperCase() : "";
}

export function buildSearchFilter({ searchType, searchQuery, name, gender, dob }) {
  switch (searchType) {
    case "abhaId":
      return {
        filter_type: "abha_id",
        filter_details: { abha_id: searchQuery },
        multipleResults: true,
      };
    case "abhaNo":
      return {
        filter_type: "abha_number",
        filter_details: { abha_number: formatAbhaNumber(searchQuery) },
        multipleResults: true,
      };
    case "PatID":
      return {
        filter_type: "patient_id",
        filter_details: { patient_id: formatAbhaNumber(searchQuery) },
        multipleResults: true,
      };
    case "uhid":
      return {
        filter_type: "uhid",
        filter_details: { uhid: searchQuery },
        multipleResults: true,
      };
    case "phoneNo":
      return {
        filter_type: "phone_number",
        filter_details: { phone_number: searchQuery },
        multipleResults: true,
      };
    case "demographics":
      return {
        filter_type: "demographics",
        filter_details: { name, gender, dob },
        multipleResults: true,
      };
    default:
      return null;
  }
}

export function mapSearchResultToPatient(apiPatient) {
  if (!apiPatient) return null;

  const contacts = [];
  if (apiPatient.phone_no) {
    contacts.push({ channel: "PHONE", type: "PRIMARY", value: apiPatient.phone_no });
  }
  if (apiPatient.email) {
    contacts.push({ channel: "EMAIL", type: "PRIMARY", value: apiPatient.email });
  }

  const line1 = apiPatient.address_line || apiPatient.address || apiPatient.locality || "";
  const district = String(apiPatient.district || apiPatient.address_district || "").toLowerCase();
  const state = String(apiPatient.state || apiPatient.address_state || "").toLowerCase();
  const city = apiPatient.city || apiPatient.address_city || "";
  const pincode = apiPatient.pincode || apiPatient.address_pincode || "";
  const hasAddress = line1 || city || district || state || pincode;

  const mapped = {
    uhid: apiPatient.patient_id || "",
    patient_id: apiPatient.patient_id || "",
    name: apiPatient.name || "",
    gender: normalizeSearchGender(apiPatient.gender),
    dob: apiPatient.dob || "",
    abhaAddress: apiPatient.abha_id != null ? (apiPatient.abha_id || "NA") : "",
    abhaNumber: apiPatient.abha_no != null ? (apiPatient.abha_no || "NA") : "",
    primaryMobile: apiPatient.phone_no || "",
    photoUrl: normalizeBase64Image(apiPatient.photo),
    contacts: contacts.length ? contacts : [{ channel: "PHONE", type: "PRIMARY", value: "" }],
  };

  if (apiPatient.marital_status) mapped.maritalStatus = String(apiPatient.marital_status).toUpperCase();
  if (apiPatient.occupation) mapped.occupation = apiPatient.occupation;
  if (apiPatient.nationality) mapped.nationality = apiPatient.nationality;
  if (apiPatient.religion) mapped.religion = apiPatient.religion;

  const emergencyName = apiPatient.emergency_contact_name;
  const emergencyPhone = apiPatient.emergency_contact_phone;
  if (emergencyName || emergencyPhone) {
    mapped.emergencyContacts = [{
      name: emergencyName || "",
      relation: apiPatient.emergency_contact_relation || "",
      phone: emergencyPhone || "",
      address: apiPatient.emergency_contact_address || "",
    }];
  }

  if (apiPatient.health_insurance) {
    mapped.encounter = {
      financialClass: "INSURANCE",
      payer: Array.isArray(apiPatient.health_insurance)
        ? apiPatient.health_insurance[0]
        : apiPatient.health_insurance,
    };
  }

  if (apiPatient.facility) {
    mapped.facility = apiPatient.facility;
  }

  if (hasAddress) {
    mapped.addresses = [{
      type: "PERMANENT",
      line1,
      city,
      district,
      state,
      country: "India",
      pincode,
    }];
  }

  return mapped;
}
